import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { useUserRole } from '@/hooks/useUserRole';
import type { HomeSummary } from '@/api/customers';
import { cacheGet, cacheSet } from '@/lib/swrCache';
import '@/styles/screens.css';

/**
 * Home (SPEC §7): two lead action tiles, four count tiles, and a "Next promotion"
 * strip. No customer names on Home (names live one tab deep in Actions/Customers).
 */
export function HomePage({ onAddCustomer }: { onAddCustomer: () => void }) {
  const navigate = useNavigate();
  const api = useApi();
  const { user } = useUserRole();
  // Instant paint from the session cache; fresh counts replace it when they land.
  const [summary, setSummary] = useState<HomeSummary | null>(() => cacheGet<HomeSummary>('home-summary'));

  useEffect(() => {
    let cancelled = false;
    api.home().then((s) => {
      if (cancelled) return;
      cacheSet('home-summary', s);
      setSummary(s);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [api]);

  const greeting = user ? `Welcome back, ${user.displayName.split(' ')[0]}` : 'Welcome back';
  const today = new Date().toLocaleDateString('en-IE', { weekday: 'long', day: 'numeric', month: 'long' });

  const counts = [
    { label: 'Outreach today', value: summary?.outreachToday, icon: 'ti-phone', accent: true },
    { label: 'Key dates', value: summary?.keyDatesThisWeek, icon: 'ti-calendar-heart' },
    { label: 'Promo matches', value: summary?.promoMatches, icon: 'ti-discount', to: '/promotions' },
    { label: 'Overdue', value: summary?.overdue, icon: 'ti-alert-triangle', warn: true },
  ];

  return (
    <div className="screen">
      <p className="screen-greeting">{today}</p>
      <h1 className="screen-title">{greeting}</h1>

      <div className="home-tiles">
        <button type="button" className="lead-tile lead-tile--primary" onClick={onAddCustomer}>
          <i className="ti ti-user-plus" aria-hidden="true" />
          Add customer
        </button>
        <button type="button" className="lead-tile lead-tile--accent" onClick={() => navigate('/lookup')}>
          <i className="ti ti-search" aria-hidden="true" />
          Look up customer
        </button>
        <button type="button" className="lead-tile lead-tile--accent" onClick={() => navigate('/customers?compose=1')}>
          <i className="ti ti-mail" aria-hidden="true" />
          Email customers
        </button>
      </div>

      <div className="count-tiles">
        {counts.map((c) => (
          <div
            key={c.label}
            className={`count-tile${c.warn ? ' count-tile--warn' : ''}${c.to ? ' count-tile--link' : ''}`}
            onClick={c.to ? () => navigate(c.to) : undefined}
            role={c.to ? 'button' : undefined}
          >
            <i className={`ti ${c.icon} count-icon${c.accent ? ' count-icon--accent' : ''}`} aria-hidden="true" />
            <span className="count-value">{c.value ?? '—'}</span>
            <span className="count-label">{c.label}</span>
          </div>
        ))}
      </div>

      {summary?.nextPromotion && (
        <button type="button" className="promo-strip" onClick={() => navigate('/promotions')}>
          <div>
            <span className="promo-kicker">Next promotion</span>
            <span className="promo-title">{summary.nextPromotion.title}</span>
          </div>
          <span className="promo-dates">
            {fmt(summary.nextPromotion.startsOn)} – {fmt(summary.nextPromotion.endsOn)}
          </span>
        </button>
      )}
    </div>
  );
}

function fmt(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IE', { day: 'numeric', month: 'short' });
}
